import React, { useId, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True for a real calendar date written yyyy-mm-dd, so "2026-9-8" and "2026-02-30" are not.
 *
 * @param {string} value
 * @returns {boolean}
 */
export const isIsoDate = (value) => {
  if (typeof value !== "string" || !ISO_DATE_PATTERN.test(value)) return false;

  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
};

/**
 * #355: a date field that shows yyyy-mm-dd to every user. A native date input renders in the
 * operating system's language instead, which is why two people in the demo saw two formats.
 * The date can be typed; the calendar button opens the browser's own picker through a hidden
 * native input, which also carries `max` so blocked dates are greyed out (#362). That picker
 * hands back yyyy-mm-dd whatever the language.
 */
const DateField = ({ label, value = "", onChange, max }) => {
  const inputId = useId();
  const pickerRef = useRef(null);

  const openPicker = () => {
    // showPicker needs the click that got us here; a browser without it gets focus instead.
    try {
      pickerRef.current?.showPicker();
    } catch {
      pickerRef.current?.focus();
    }
  };

  return (
    <div className="w-full">
      <label htmlFor={inputId} className="block text-sm font-bold mb-2">
        {label}
      </label>
      <div className="relative w-full">
        <input
          id={inputId}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="yyyy-mm-dd"
          maxLength={10}
          className="w-full pr-10 icon-right input-default"
        />
        <button
          type="button"
          onClick={openPicker}
          aria-label={`Choose ${String(label).replace(/\s*\*$/, "")} in a calendar`}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-black"
        >
          <FontAwesomeIcon icon={faCalendar} />
        </button>
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          value={isIsoDate(value) ? value : ""}
          max={max}
          onChange={(e) => onChange(e.target.value)}
          className="absolute right-0 bottom-0 w-px h-px opacity-0 pointer-events-none"
        />
      </div>
    </div>
  );
};

export default DateField;
